/**
 * Enhanced Job Creation and Editing Form
 * 
 * A comprehensive multi-tab form for creating and editing procurement jobs.
 * This component manages the complete job creation workflow including:
 * - Basic job information and categorization
 * - Specification template selection and customization
 * - Delivery location configuration
 * 
 * Features:
 * - Tab-based navigation with validation on tab change
 * - Real-time form validation with error display
 * - Specification template library with filtering
 * - Delivery template selection with geocoding
 * - Create custom specifications and delivery locations inline
 * - Preview specifications and delivery details before saving
 * - Auto-save on tab navigation
 * - Support for both creation and editing modes
 * 
 * Workflow Stages:
 * 1. Basics: Job title, description, category, due date, deadline type
 * 2. Specifications: Select from template library or create custom specs
 * 3. Delivery: Configure delivery locations from templates or create new
 * 
 * @module EnhancedJobForm
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowRight, FileText, FileEdit, MapPin, Plus, Eye, CalendarIcon, AlertCircle, Info } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useSpecifications } from '@/hooks/useSpecifications';
import { useDeliveryTemplates } from '@/hooks/useDeliveryTemplates';
import { useClientProfile } from '@/hooks/useClientProfile';
import { JOB_CATEGORIES, parseCurrency, formatCurrency, PriorityLevel, ProcurementMethod, Job, JobCategory, SpecificationType } from '@/lib/types/job';
import { MasterSpecificationBuilder } from '@/components/specifications/MasterSpecificationBuilder';
import { TemplateEditor } from '@/components/templates/TemplateEditor';
import { DialogDescription } from '@/components/ui/dialog';
import { SpecificationDataRenderer } from '@/components/specifications/SpecificationDataRenderer';
import { DeadlineTypeBadge, getDeadlineTypeDescription, DeadlineType } from './DeadlineTypeBadge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Search } from 'lucide-react';
// Note: Legacy geocoding removed - address validation now uses Smarty API
import { cn } from '@/lib/utils';
import { ProjectSelector } from '@/components/projects/ProjectSelector';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { logJobChanges } from '@/lib/utils/trackJobChanges';

/**
 * Props for the EnhancedJobForm component
 */
interface EnhancedJobFormProps {
  /** Optional existing job data for edit mode */
  existingJob?: Job;
  /** Optional callback invoked when job is saved (if not advancing workflow) */
  onSave?: (job: Job) => void;
  /** Optional read-only mode */
  readOnly?: boolean;
  /** Optional initial tab to display */
  initialTab?: 'basics' | 'specifications' | 'delivery';
}

/**
 * EnhancedJobForm Component
 * 
 * Multi-step form for creating or editing procurement jobs with comprehensive
 * specification and delivery configuration.
 * 
 * The form automatically handles:
 * - Validation at each step
 * - Specification template selection and application
 * - Delivery location geocoding
 * - Real-time save on tab navigation
 * - Navigation to job view on completion
 * 
 * @param props - Component props
 * @param props.existingJob - Existing job data for editing (optional)
 * @param props.onSave - Callback when job is saved without advancing workflow (optional)
 * 
 * @example
 * ```tsx
 * // Creating a new job
 * <EnhancedJobForm />
 * 
 * // Editing existing job
 * <EnhancedJobForm
 *   existingJob={job}
 *   onSave={(updatedJob) => console.log('Job saved:', updatedJob)}
 * />
 * ```
 */
export function EnhancedJobForm({ existingJob, onSave, readOnly = false, initialTab = 'basics' }: EnhancedJobFormProps) {
  // Check if job is in a finalized state
  const isFinalized = existingJob && ['accepted', 'rejected', 'closed'].includes(existingJob.status);
  const isReadOnly = readOnly || isFinalized;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const { profile: clientProfile } = useClientProfile();
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [createdJobId, setCreatedJobId] = useState<string | null>(existingJob?.id || null);
  
  const [formData, setFormData] = useState({
    title: existingJob?.title || '',
    description: existingJob?.description || '',
    category: (existingJob?.category || '') as any,
    art_files_ready_date: existingJob?.art_files_ready_date ? new Date(existingJob.art_files_ready_date) : undefined as Date | undefined,
    due_date: existingJob?.due_date ? new Date(existingJob.due_date) : undefined as Date | undefined,
    project_id: (existingJob as any)?.project_id || '',
    project_name: existingJob?.project_name || '',
    internal_reference: existingJob?.internal_reference || '',
    priority_level: existingJob?.priority_level || 'medium' as PriorityLevel,
    procurement_method: existingJob?.procurement_method || 'rfq' as ProcurementMethod,
    client_notes: existingJob?.client_notes || '',
    estimated_start_date: existingJob?.estimated_start_date ? new Date(existingJob.estimated_start_date) : undefined as Date | undefined,
    estimated_completion_date: existingJob?.estimated_completion_date ? new Date(existingJob.estimated_completion_date) : undefined as Date | undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedSpecTemplates, setSelectedSpecTemplates] = useState<string[]>([]);
  const [selectedDeliveryTemplates, setSelectedDeliveryTemplates] = useState<string[]>([]);
  const [existingSpecifications, setExistingSpecifications] = useState<any[]>([]);
  
  // Filter states for specifications tab
  const [specCategoryFilter, setSpecCategoryFilter] = useState<JobCategory | 'all'>('all');
  const [specTypeFilter, setSpecTypeFilter] = useState<SpecificationType | 'all'>('all');

  const [openSpecModal, setOpenSpecModal] = useState(false);
  const [openDeliveryModal, setOpenDeliveryModal] = useState(false);
  const [viewSpecModal, setViewSpecModal] = useState<{open: boolean, spec: any}>({open: false, spec: null});
  const [viewDeliveryModal, setViewDeliveryModal] = useState<{open: boolean, location: any}>({open: false, location: null});
  const [deadlineType, setDeadlineType] = useState<DeadlineType>('flexible_date');
  

  // Track job started event when creating a new job
  useEffect(() => {
    if (!existingJob && !createdJobId) {
      analytics.track({
        name: ANALYTICS_EVENTS.JOB_STARTED,
        category: 'engagement',
        properties: {},
      });
    }
  }, []); // Only run once on mount

  // Fetch ALL specifications (not filtered by job category)
  const { specifications: allSpecTemplates, loading: specLoading, createSpecification: createSpecTemplate } = useSpecifications({});

  // Helper to compute specification type based on ownership
  const getComputedSpecType = (template: any): SpecificationType => {
    // Personal = created by current user
    if (template.created_by_user_id === user?.id) {
      return 'personal';
    }
    // My Organization = same org, different creator
    if (template.organization_id === clientProfile?.organization_id) {
      return 'my_organization';
    }
    // Fallback (shouldn't happen due to RLS)
    return 'personal';
  };

  // Filter specifications client-side based on selected filters
  const specTemplates = allSpecTemplates.filter(template => {
    if (specCategoryFilter !== 'all' && template.category !== specCategoryFilter) {
      return false;
    }
    // Type filter - use computed type instead of stored value
    if (specTypeFilter !== 'all') {
      const computedType = getComputedSpecType(template);
      if (computedType !== specTypeFilter) {
        return false;
      }
    }
    return true;
  });

  const getTypeLabel = (type: SpecificationType) => {
    const labels: Record<SpecificationType, string> = {
      my_organization: 'My Organization',
      personal: 'Personal'
    };
    return labels[type];
  };

  const { templates: deliveryTemplates, loading: deliveryLoading, createTemplate: createDeliveryTemplate } = useDeliveryTemplates();

  // Load existing specifications when editing a job
  useEffect(() => {
    if (existingJob?.id) {
      const loadExistingSpecifications = async () => {
        try {
          const { data: specs, error } = await supabase
            .from('job_specifications')
            .select('*')
            .eq('job_id', existingJob.id);

          if (error) throw error;

          setExistingSpecifications(specs || []);
          
          // Set selected spec templates based on existing specifications
          const templateIds = specs?.map(spec => spec.source_template_id).filter(Boolean) || [];
          setSelectedSpecTemplates(templateIds);

          // Load existing delivery details
          const { data: deliveryDetailsList, error: deliveryError } = await supabase
            .from('job_delivery_details')
            .select('*')
            .eq('job_id', existingJob.id)
            .order('created_at', { ascending: true });

          if (deliveryDetailsList && deliveryDetailsList.length > 0) {
            const templateIds = deliveryDetailsList
              .map(detail => detail.source_template_id)
              .filter(Boolean);
            setSelectedDeliveryTemplates(templateIds);
          }
        } catch (err) {
          console.error('Error loading existing job details:', err);
        }
      };

      loadExistingSpecifications();
    }
  }, [existingJob?.id]);


  const validateCurrentTab = () => {
    const newErrors: Record<string, string> = {};

    if (currentTab === 'basics') {
      if (formData.title.length < 5 || formData.title.length > 255) {
        newErrors.title = 'Title must be between 5 and 255 characters';
      }
      if (formData.description.length < 20 || formData.description.length > 5000) {
        newErrors.description = 'Description must be between 20 and 5000 characters';
      }
      if (!formData.category) {
        newErrors.category = 'Category is required';
      }
      if (!formData.art_files_ready_date) {
        newErrors.art_files_ready_date = 'Art Files Ready Date is required';
      } else {
        if (formData.art_files_ready_date <= new Date()) {
          newErrors.art_files_ready_date = 'Art Files Ready Date must be in the future';
        }
      }
      if (!formData.due_date) {
        newErrors.due_date = 'Due date is required';
      } else {
        if (formData.due_date <= new Date()) {
          newErrors.due_date = 'Due date must be in the future';
        }
        // Validate that due date is after art files ready date
        if (formData.art_files_ready_date) {
          if (formData.due_date <= formData.art_files_ready_date) {
            newErrors.due_date = 'Due date must be after Art Files Ready Date';
          }
        }
      }
    }

    if (currentTab === 'specifications') {
      if (selectedSpecTemplates.length === 0) {
        newErrors.specifications = 'Please select at least one specification template';
      }
    }

    if (currentTab === 'delivery') {
      if (selectedDeliveryTemplates.length === 0) {
        newErrors.delivery = 'Please select at least one delivery location';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTabChange = (tab: string) => {
    if (validateCurrentTab()) {
      // Track tab completion
      analytics.track({
        name: ANALYTICS_EVENTS.JOB_TAB_COMPLETED,
        category: 'engagement',
        properties: {
          tab_name: currentTab,
          job_id: existingJob?.id || createdJobId,
        },
      });
      setCurrentTab(tab as 'basics' | 'specifications' | 'delivery');
    }
  };

  const handleSave = async (shouldAdvanceWorkflow = false) => {
    if (!validateCurrentTab()) {
      return;
    }

    setLoading(true);

    try {
      if (!isAuthenticated || !user) {
        throw new Error('User not authenticated');
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        toast({
          title: "Profile Setup Required",
          description: "Please complete your profile setup before creating jobs.",
          variant: "destructive",
        });
        navigate('/profile/organization');
        return;
      }

      const jobData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        budget_cents: 0, // Default to 0 since budget is not collected
        art_files_ready_date: formData.art_files_ready_date ? formData.art_files_ready_date.toISOString() : null,
        due_date: formData.due_date ? formData.due_date.toISOString() : null,
        project_id: formData.project_id || null,
        project_name: formData.project_name || null,
        internal_reference: formData.internal_reference || null,
        priority_level: formData.priority_level,
        procurement_method: formData.procurement_method,
        client_notes: formData.client_notes || null,
        estimated_start_date: formData.estimated_start_date ? formData.estimated_start_date.toISOString() : null,
        estimated_completion_date: formData.estimated_completion_date ? formData.estimated_completion_date.toISOString() : null,
        organization_id: profile.organization_id,
        created_by_user_id: user.id, // Track job creator for RBAC
        workflow_stage: shouldAdvanceWorkflow ? 'specifications' : 'draft',
        requirements_complete: false,
        specifications_required: true,
        delivery_details_required: true,
      };

      let result;
      if (existingJob || createdJobId) {
        // Update existing job
        const jobId = existingJob?.id || createdJobId;
        
        // Fetch current job state before update for change tracking
        const { data: currentJob } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', jobId)
          .single();
        
        const { data, error } = await supabase
          .from('jobs')
          .update(jobData)
          .eq('id', jobId)
          .select()
          .single();
        if (error) throw error;
        result = data;
        
        // Log changes to audit log
        if (currentJob && user) {
          await logJobChanges(jobId!, user.id, currentJob, jobData);
        }
      } else {
        // Create new job only on first save
        const { data, error } = await supabase
          .from('jobs')
          .insert(jobData)
          .select()
          .single();
        if (error) throw error;
        result = data;
        setCreatedJobId(result.id); // Track the created job ID
      }

      // Handle specifications and delivery details - delete and recreate when updating
      if (result && (existingJob || createdJobId)) {
        // Delete existing specifications when updating
        const { error: deleteSpecError, count: deletedSpecsCount } = await supabase
          .from('job_specifications')
          .delete()
          .eq('job_id', result.id);

        if (deleteSpecError) {
          console.error('Failed to delete job specifications:', deleteSpecError);
          throw deleteSpecError;
        }
        console.log(`Deleted ${deletedSpecsCount || 0} specification records for job ${result.id}`);

        // Delete existing delivery details when updating
        const { error: deleteDeliveryError, count: deletedDeliveryCount } = await supabase
          .from('job_delivery_details')
          .delete()
          .eq('job_id', result.id);

        if (deleteDeliveryError) {
          console.error('Failed to delete delivery details:', deleteDeliveryError);
          throw deleteDeliveryError;
        }
        console.log(`Deleted ${deletedDeliveryCount || 0} delivery detail records for job ${result.id}`);
      }

      // Create specifications from templates
      if (selectedSpecTemplates.length > 0 && result) {
        // Batch insert all specifications to avoid race conditions
        const specInserts = selectedSpecTemplates.map((templateId, index) => {
          const template = specTemplates.find(t => t.id === templateId);
          if (!template) return null;
          
          return {
            job_id: result.id,
            source_template_id: template.id,
            specification_name: template.specification_name,
            specification_type: template.specification_type,
            specification_order: index + 1, // Set order based on selection
            specification_document_id: (template as any).specification_document_id,
            content: template.content,
            sections: template.sections,
            custom_requirements: {},
            is_mandatory: true,
            compliance_level: 'required',
            created_by_user_id: user.id,
            last_updated_by_user_id: user.id,
          };
        }).filter(Boolean);

        if (specInserts.length > 0) {
          const { error: specError } = await supabase
            .from('job_specifications')
            .upsert(specInserts as any, { onConflict: 'job_id,specification_order' });
          
          if (specError) throw specError;

          // Increment usage count for each specification template used
          for (const templateId of selectedSpecTemplates) {
            const { data: currentSpec } = await supabase
              .from('specifications')
              .select('usage_count')
              .eq('id', templateId)
              .single();
            
            if (currentSpec) {
              await supabase
                .from('specifications')
                .update({ usage_count: (currentSpec.usage_count || 0) + 1 })
                .eq('id', templateId);
            }
          }
        }
      }

      // Create delivery details from templates
      if (selectedDeliveryTemplates.length > 0 && result) {
        // Prepare templates for processing
        const templatesToProcess = selectedDeliveryTemplates
          .map(templateId => deliveryTemplates.find(t => t.id === templateId))
          .filter(Boolean);
        
        // Build delivery inserts from templates
        const deliveryInserts: any[] = [];
        
        templatesToProcess.forEach((template, index) => {
          if (!template) return;
          
          // Address validation is now handled via Smarty API during template creation
          // Coordinates are kept for backward compatibility but no longer geocoded
          deliveryInserts.push({
            job_id: result.id,
            source_template_id: template.id,
            delivery_name: template.location_name,
            location_type: template.location_type,
            address_line1: template.address_line1,
            address_line2: template.address_line2,
            city: template.city,
            state_province: template.state_province,
            postal_code: template.postal_code,
            country: template.country,
            latitude: template.latitude ?? null,
            longitude: template.longitude ?? null,
            geocode_precision: template.geocode_precision ?? null,
            contact_name: template.contact_name,
            contact_phone: template.contact_phone,
            contact_email: template.contact_email,
            special_instructions: template.special_instructions,
            access_requirements: template.access_requirements,
            delivery_hours: template.delivery_hours,
            preferred_carriers: template.preferred_carriers,
            restricted_carriers: template.restricted_carriers,
            dock_information: template.dock_information,
            signature_required: false,
            id_required: false,
            appointment_required: false,
            deadline_type: deadlineType,
            delivery_order: index + 1,
            created_by_user_id: user.id,
            last_updated_by_user_id: user.id,
          });
        });

        if (deliveryInserts.length > 0) {
          const { error: deliveryError } = await supabase
            .from('job_delivery_details')
            .insert(deliveryInserts);
          
          if (deliveryError) throw deliveryError;

          // Increment usage count for each delivery template used
          for (const templateId of selectedDeliveryTemplates) {
            const { data: currentTemplate } = await supabase
              .from('delivery_location_templates')
              .select('usage_count')
              .eq('id', templateId)
              .single();
            
            if (currentTemplate) {
              await supabase
                .from('delivery_location_templates')
                .update({ usage_count: (currentTemplate.usage_count || 0) + 1 })
                .eq('id', templateId);
            }
          }
          
          // Show summary of delivery locations added
          if (deliveryInserts.length > 0) {
            toast({
              title: 'Delivery Locations Added',
              description: `📍 Added ${deliveryInserts.length} delivery location(s)`,
            });
          }
        }
      }

      const isUpdating = existingJob || createdJobId;
      toast({
        title: isUpdating ? 'Job updated' : 'Job created',
        description: `Job ${result.public_id} has been ${isUpdating ? 'updated' : 'created'}`,
      });

      if (onSave && !shouldAdvanceWorkflow) {
        onSave(result as Job);
      } else if (shouldAdvanceWorkflow) {
        // Advance to the next tab in the workflow
        const tabOrder = ['basics', 'specifications', 'delivery'] as const;
        const currentIndex = tabOrder.indexOf(currentTab);
        if (currentIndex < tabOrder.length - 1) {
          setCurrentTab(tabOrder[currentIndex + 1]);
        } else {
          // Final tab - navigate to job view
          navigate(`/jobs/${result.public_id}`);
        }
      } else {
        navigate(`/jobs/${result.public_id}`);
      }
    } catch (err) {
      console.error('Error saving job:', err);
      
      // Extract meaningful error message from Supabase error
      const message =
        typeof err === 'object' && err && 'message' in err 
          ? (err as any).message 
          : err instanceof Error 
            ? err.message 
            : 'An unexpected error occurred';
      
      toast({
        title: 'Error saving job',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="grid w-full grid-cols-3 min-w-[500px] sm:min-w-0">
            <TabsTrigger value="basics" className="flex items-center gap-2 text-xs sm:text-sm">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Basics</span>
            </TabsTrigger>
            <TabsTrigger value="specifications" className="flex items-center gap-2 text-xs sm:text-sm">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Specs</span>
            </TabsTrigger>
            <TabsTrigger value="delivery" className="flex items-center gap-2 text-xs sm:text-sm">
              <MapPin className="h-4 w-4" />
              <span className="hidden sm:inline">Delivery</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="basics" className="space-y-6">
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter a descriptive job title..."
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1">{formData.title.length}/255 characters</p>
                </div>

                <div>
                  <Label htmlFor="project_id">Project</Label>
                  <ProjectSelector
                    value={formData.project_id}
                    onChange={(value) => setFormData({ ...formData, project_id: value })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">Organize this job by assigning it to a project</p>
                </div>

                <div>
                  <Label htmlFor="internal_reference">Internal Reference</Label>
                  <Input
                    id="internal_reference"
                    value={formData.internal_reference}
                    onChange={(e) => setFormData({ ...formData, internal_reference: e.target.value })}
                    placeholder="Internal job ID or reference..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Provide a detailed description of the job requirements..."
                  rows={6}
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_CATEGORIES.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-sm text-red-600 mt-1">{errors.category}</p>}
                </div>

                <div>
                  <Label htmlFor="art_files_ready_date">Art Files Ready Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.art_files_ready_date && "text-muted-foreground",
                          errors.art_files_ready_date && "border-red-500"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.art_files_ready_date ? format(formData.art_files_ready_date, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.art_files_ready_date}
                        onSelect={(date) => setFormData({ ...formData, art_files_ready_date: date })}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.art_files_ready_date && <p className="text-sm text-red-600 mt-1">{errors.art_files_ready_date}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.due_date && "text-muted-foreground",
                          errors.due_date && "border-red-500"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.due_date ? format(formData.due_date, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.due_date}
                        onSelect={(date) => setFormData({ ...formData, due_date: date })}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.due_date && <p className="text-sm text-red-600 mt-1">{errors.due_date}</p>}
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold">Deadline Type *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select how flexible the delivery deadline is for this job
                </p>
                <TooltipProvider>
                  <RadioGroup value={deadlineType} onValueChange={(value) => setDeadlineType(value as DeadlineType)}>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="hard_deadline" id="hard_deadline" />
                        <Label htmlFor="hard_deadline" className="flex items-center gap-2 cursor-pointer font-normal">
                          Hard Deadline
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{getDeadlineTypeDescription('hard_deadline')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="flexible_date" id="flexible_date" />
                        <Label htmlFor="flexible_date" className="flex items-center gap-2 cursor-pointer font-normal">
                          Standard Deadline
                          <Badge variant="secondary" className="ml-1">Default</Badge>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{getDeadlineTypeDescription('flexible_date')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="specifications" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Specifications</CardTitle>
                <Dialog open={openSpecModal} onOpenChange={setOpenSpecModal}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="mr-2 h-4 w-4" />
                      Create Custom Spec
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Custom Specification</DialogTitle>
                      <DialogDescription>
                        Select a product category to load the master specification template
                      </DialogDescription>
                    </DialogHeader>
                    <MasterSpecificationBuilder 
                      onSave={(specData) => {
                        createSpecTemplate(specData).then(() => {
                          setOpenSpecModal(false);
                        });
                      }}
                      onCancel={() => setOpenSpecModal(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {specLoading ? (
                <div className="text-center py-4">Loading specification templates...</div>
              ) : (
                <div className="space-y-4">
                  {/* Filter Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="spec-category-filter">Filter by Category</Label>
                      <Select value={specCategoryFilter} onValueChange={(v) => setSpecCategoryFilter(v as JobCategory | 'all')}>
                        <SelectTrigger id="spec-category-filter" className="bg-background">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          <SelectItem value="all">All Categories</SelectItem>
                          {JOB_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="spec-type-filter">Filter by Type</Label>
                      <Select value={specTypeFilter} onValueChange={(v) => setSpecTypeFilter(v as SpecificationType | 'all')}>
                        <SelectTrigger id="spec-type-filter" className="bg-background">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="my_organization">My Organization</SelectItem>
                          <SelectItem value="personal">Personal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Select specification templates to include with this job:
                  </p>

                  {/* Error message */}
                  {errors.specifications && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <p className="text-sm text-red-600">{errors.specifications}</p>
                    </div>
                  )}
                  
                  {/* Show currently selected specifications */}
                  {selectedSpecTemplates.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Selected Specifications ({selectedSpecTemplates.length})</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedSpecTemplates.map((templateId) => {
                          const template = specTemplates.find(t => t.id === templateId);
                          return template ? (
                            <Badge key={templateId} variant="default" className="flex items-center gap-1">
                              {template.specification_name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSpecTemplates(selectedSpecTemplates.filter(id => id !== templateId));
                                }}
                                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                              >
                                ×
                              </button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {specTemplates.map((template) => (
                      <Card
                        key={template.id}
                        className={`transition-colors ${
                          selectedSpecTemplates.includes(template.id) ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              {(template as any).specification_document_id ? (
                                <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                              ) : (
                                <FileEdit className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              )}
                              <h4 className="font-medium">{template.specification_name}</h4>
                            </div>
                            <Badge variant="outline">{getTypeLabel(template.specification_type as SpecificationType)}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{template.specification_description}</p>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{template.category.replace('_', ' ')}</Badge>
                              <Badge variant="outline" className="text-xs">
                                {(template as any).specification_document_id ? 'Document' : 'Form'}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">Used {template.usage_count} times</span>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setViewSpecModal({open: true, spec: template})}
                              className="flex items-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              View Detail
                            </Button>
                            <Button
                              variant={selectedSpecTemplates.includes(template.id) ? "destructive" : "default"}
                              size="sm"
                              onClick={() => {
                                if (selectedSpecTemplates.includes(template.id)) {
                                  setSelectedSpecTemplates(selectedSpecTemplates.filter(id => id !== template.id));
                                } else {
                                  setSelectedSpecTemplates([...selectedSpecTemplates, template.id]);
                                }
                              }}
                            >
                              {selectedSpecTemplates.includes(template.id) ? "Remove" : "Add"}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {specTemplates.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No specifications match the selected filters. Try adjusting your filters or create a custom specification.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="delivery" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Delivery Details</CardTitle>
                <Dialog open={openDeliveryModal} onOpenChange={setOpenDeliveryModal}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="mr-2 h-4 w-4" />
                      Create Custom Location
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Custom Delivery Location</DialogTitle>
                    </DialogHeader>
                    <TemplateEditor 
                      type="delivery" 
                      onSave={(template) => {
                        createDeliveryTemplate(template).then(() => {
                          setOpenDeliveryModal(false);
                        });
                      }} 
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {deliveryLoading ? (
                <div className="text-center py-4">Loading delivery templates...</div>
              ) : (
                <div className="space-y-6">
                  {selectedDeliveryTemplates.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Selected Delivery Locations ({selectedDeliveryTemplates.length})</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedDeliveryTemplates.map((templateId) => {
                          const template = deliveryTemplates.find(t => t.id === templateId);
                          return template ? (
                            <Badge key={templateId} variant="default" className="flex items-center gap-1 px-3 py-1">
                              {template.location_name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDeliveryTemplates(selectedDeliveryTemplates.filter(id => id !== templateId));
                                }}
                                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                              >
                                ×
                              </button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Select one or more delivery locations for this job:
                    </p>

                    {/* Error message */}
                    {errors.delivery && (
                      <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <p className="text-sm text-red-600">{errors.delivery}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {deliveryTemplates.map((template) => (
                        <Card
                          key={template.id}
                          className={`transition-colors ${
                            selectedDeliveryTemplates.includes(template.id) ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium">{template.location_name}</h4>
                              <Badge variant="outline">{template.location_type.replace('_', ' ')}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <p>{template.address_line1}</p>
                              {template.address_line2 && <p>{template.address_line2}</p>}
                              <p>{template.city}, {template.state_province} {template.postal_code}</p>
                            </div>
                            <div className="mt-3">
                              <span className="text-xs text-muted-foreground">Used {template.usage_count} times</span>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setViewDeliveryModal({open: true, location: template})}
                                  className="flex items-center gap-1"
                                >
                                  <Eye className="h-3 w-3" />
                                  View Detail
                                </Button>
                                <Button
                                  type="button"
                                  variant={selectedDeliveryTemplates.includes(template.id) ? "destructive" : "default"}
                                  size="sm"
                                  onClick={() => {
                                    if (selectedDeliveryTemplates.includes(template.id)) {
                                      setSelectedDeliveryTemplates(selectedDeliveryTemplates.filter(id => id !== template.id));
                                    } else {
                                      setSelectedDeliveryTemplates([...selectedDeliveryTemplates, template.id]);
                                    }
                                  }}
                                >
                                  {selectedDeliveryTemplates.includes(template.id) ? "Remove" : "Add"}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    {deliveryTemplates.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No delivery templates available.
                      </div>
                    )}
                  </div>

                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Specification Detail Modal */}
      <Dialog open={viewSpecModal.open} onOpenChange={(open) => setViewSpecModal({open, spec: null})}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Specification Details</DialogTitle>
          </DialogHeader>
          {viewSpecModal.spec && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Name</Label>
                  <p className="text-sm">{viewSpecModal.spec.specification_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Type</Label>
                  <Badge variant="outline">{getTypeLabel(viewSpecModal.spec.specification_type as SpecificationType)}</Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Badge variant="secondary">{viewSpecModal.spec.category.replace('_', ' ')}</Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium">Usage Count</Label>
                  <p className="text-sm">{viewSpecModal.spec.usage_count} times</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <p className="text-sm text-muted-foreground mt-1">{viewSpecModal.spec.specification_description}</p>
              </div>

              {viewSpecModal.spec.sections && viewSpecModal.spec.sections.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Sections</Label>
                  <div className="mt-2 space-y-3">
                    {viewSpecModal.spec.sections.map((section: any, index: number) => (
                      <Card key={index} className="p-4">
                        <h4 className="font-medium mb-2">{section.title}</h4>
                        <p className="text-sm text-muted-foreground">{section.content}</p>
                        {section.requirements && section.requirements.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Requirements:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {section.requirements.map((req: string, reqIndex: number) => (
                                <li key={reqIndex} className="flex items-start gap-1">
                                  <span>•</span>
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {viewSpecModal.spec.content && Object.keys(viewSpecModal.spec.content).length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Specification Details</Label>
                  <div className="mt-2 text-sm bg-muted p-4 rounded">
                    <SpecificationDataRenderer content={viewSpecModal.spec.content} />
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setViewSpecModal({open: false, spec: null})}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delivery Location Detail Modal */}
      <Dialog open={viewDeliveryModal.open} onOpenChange={(open) => setViewDeliveryModal({open, location: null})}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delivery Location Details</DialogTitle>
          </DialogHeader>
          {viewDeliveryModal.location && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Location Name</Label>
                  <p className="text-sm mt-1">{viewDeliveryModal.location.location_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Location Type</Label>
                  <div className="mt-1">
                    <Badge variant="outline">{viewDeliveryModal.location.location_type.replace('_', ' ')}</Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Address</Label>
                <div className="text-sm text-muted-foreground mt-1 space-y-1">
                  <p>{viewDeliveryModal.location.address_line1}</p>
                  {viewDeliveryModal.location.address_line2 && <p>{viewDeliveryModal.location.address_line2}</p>}
                  <p>{viewDeliveryModal.location.city}, {viewDeliveryModal.location.state_province} {viewDeliveryModal.location.postal_code}</p>
                  <p>{viewDeliveryModal.location.country}</p>
                </div>
              </div>

              {(viewDeliveryModal.location.contact_name || viewDeliveryModal.location.contact_phone || viewDeliveryModal.location.contact_email) && (
                <div>
                  <Label className="text-sm font-medium">Contact Information</Label>
                  <div className="text-sm text-muted-foreground mt-1 space-y-1">
                    {viewDeliveryModal.location.contact_name && <p>Name: {viewDeliveryModal.location.contact_name}</p>}
                    {viewDeliveryModal.location.contact_phone && <p>Phone: {viewDeliveryModal.location.contact_phone}</p>}
                    {viewDeliveryModal.location.contact_email && <p>Email: {viewDeliveryModal.location.contact_email}</p>}
                  </div>
                </div>
              )}

              {viewDeliveryModal.location.special_instructions && (
                <div>
                  <Label className="text-sm font-medium">Special Instructions</Label>
                  <p className="text-sm text-muted-foreground mt-1">{viewDeliveryModal.location.special_instructions}</p>
                </div>
              )}

              {viewDeliveryModal.location.access_requirements && (
                <div>
                  <Label className="text-sm font-medium">Access Requirements</Label>
                  <p className="text-sm text-muted-foreground mt-1">{viewDeliveryModal.location.access_requirements}</p>
                </div>
              )}

              {viewDeliveryModal.location.dock_information && (
                <div>
                  <Label className="text-sm font-medium">Dock Information</Label>
                  <p className="text-sm text-muted-foreground mt-1">{viewDeliveryModal.location.dock_information}</p>
                </div>
              )}

              {viewDeliveryModal.location.delivery_hours && Object.keys(viewDeliveryModal.location.delivery_hours).length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Delivery Hours</Label>
                  <Card className="p-4 mt-2">
                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
                      {JSON.stringify(viewDeliveryModal.location.delivery_hours, null, 2)}
                    </pre>
                  </Card>
                </div>
              )}

              {(viewDeliveryModal.location.preferred_carriers?.length > 0 || viewDeliveryModal.location.restricted_carriers?.length > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  {viewDeliveryModal.location.preferred_carriers?.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium">Preferred Carriers</Label>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {viewDeliveryModal.location.preferred_carriers.map((carrier: string, index: number) => (
                          <Badge key={index} variant="secondary">{carrier}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewDeliveryModal.location.restricted_carriers?.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium">Restricted Carriers</Label>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {viewDeliveryModal.location.restricted_carriers.map((carrier: string, index: number) => (
                          <Badge key={index} variant="destructive">{carrier}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-sm font-medium">Usage Statistics</Label>
                <p className="text-sm text-muted-foreground mt-1">Used {viewDeliveryModal.location.usage_count} times</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setViewDeliveryModal({open: false, location: null})}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/jobs')}>
          Cancel
        </Button>
        <div className="flex gap-3">
          <Button onClick={() => handleSave(false)} disabled={loading}>
            <Save className="mr-2 h-4 w-4" />
            {loading ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={loading}>
            <ArrowRight className="mr-2 h-4 w-4" />
            {loading ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}